import type { Dispatch, SetStateAction } from 'react';
import type { PayPalElementProps } from '@adyen/adyen-web/dist/types/components/PayPal/types';
import { isStar2StoreLocale, storeKeyMapper } from '@shared/utils';
import { getOnOrderPlacedCaptureConfig } from '@shared/utils/src/adyen';
import type { Cart } from '../../../codegen/types';
import type { PlaceOrderMutationFn } from '../../../types';
import { PaymentMethodTypes } from '../../../utils/adyen/adyen';
import { sendAddPaymentInfoEvent } from '../../../utils/gtm/events/add_payment_info/sendAddPaymentInfoEvent';
import { AddPaymentInfoEventPaymentTypes } from '../../../utils/gtm/types/addPaymentInfoEventPaymentTypes';
import { localeToUpperCase } from '../../../utils/internationalization';
import { newRelicAvailabilityChecker } from '../../../utils/newrelic';
import { setPaymentMethodInStorage } from '../utils/setPaymentMethodInStorage';

// Adyen docs - web components - PayPal
// https://docs.adyen.com/payment-methods/paypal/web-component/

export interface CreatePayPalConfigProps {
  locale: string;
  miniCart: Cart | Record<string, never>;
  cartId: string;
  baseUrl: string;
  setIsPaymentBtnDisabled: Dispatch<SetStateAction<boolean>>;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  setPaymentError: Dispatch<SetStateAction<boolean>>;
  validateDiscountsBeforePayment: () => Promise<boolean>;
  placeOrderMutation: PlaceOrderMutationFn;
  onBeforeSubmit: (data: unknown, actions: unknown) => Promise<void>;
}

/**
 * Determines the intent ('capture'|'authorized') for paypal payments.
 */
const getPaypalPaymentIntent = (locale: string): 'capture' | 'authorize' => {
  if (
    getOnOrderPlacedCaptureConfig(storeKeyMapper(locale))?.has(PaymentMethodTypes.PAYPAL) ||
    isStar2StoreLocale(locale)
  ) {
    return 'capture';
  }

  return 'authorize';
};

export const createPayPalConfig = ({
  locale,
  miniCart,
  cartId,
  baseUrl,
  setIsPaymentBtnDisabled,
  setPaymentInProgress,
  setPaymentError,
  validateDiscountsBeforePayment,
  placeOrderMutation,
  onBeforeSubmit,
}: CreatePayPalConfigProps): PayPalElementProps => ({
  intent: getPaypalPaymentIntent(locale),
  blockPayPalPayLaterButton: true,
  blockPayPalCreditButton: true,
  showPayButton: true,
  style: {
    color: 'blue',
    label: 'checkout',
  },
  blockPayPalVenmoButton: true,
  locale: localeToUpperCase(locale),
  beforeSubmit: async (data, _, actions) => {
    try {
      setIsPaymentBtnDisabled(true);
      setPaymentInProgress(true);

      const isDiscountValid = await validateDiscountsBeforePayment();

      if (!isDiscountValid) {
        actions.reject();
        setPaymentInProgress(false);
        setIsPaymentBtnDisabled(false);
        return;
      }

      const result = await placeOrderMutation();

      if (result?.data?.placeOrder.accepted) {
        sendAddPaymentInfoEvent(miniCart, AddPaymentInfoEventPaymentTypes.PAYPAL, cartId, baseUrl);
        setPaymentMethodInStorage(PaymentMethodTypes.PAYPAL, 'PayPal');
        await onBeforeSubmit(data, actions);
      } else {
        actions.reject();
        setPaymentError(true);
        setIsPaymentBtnDisabled(false);
        setPaymentInProgress(false);
      }
    } catch (error) {
      actions.reject();
      setPaymentError(true);
      setIsPaymentBtnDisabled(false);
      setPaymentInProgress(false);
      console.error('Error during PayPal payment processing:', error);
      newRelicAvailabilityChecker(() => {
        window.newrelic.noticeError(new Error(`Error during PayPal payment processing: ${error}`));
      });
    }
  },
});
